// /admin/quality-gates — list + edit gate conditions (T121).
//
// Layout:
//   - Header with eyebrow, title, and link back to Settings.
//   - List of quality gates; each gate is a card with an expand/collapse toggle.
//   - Expanded card shows conditions in a table with edit + delete per row,
//     plus an "Add condition" form row at the bottom.
//   - Create gate button opens a dialog. Rename/delete per gate via action buttons.
//
// Condition fields: metric (select), op (GT/GTE/LT/LTE/EQ select),
// threshold (number input), on_new_code (checkbox toggle).

import { FormEvent, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Plus,
  Settings,
  Shield,
  Star,
  Trash2,
} from 'lucide-react';
import {
  ApiError,
  QualityGate,
  QualityGateCondition,
  QualityGateConditionCreate,
  QualityGateConditionUpdate,
  QualityGateDetail,
  adminQualityGatesApi,
  fetcher,
} from '../lib/api';
import { PageHeader, Table, TBody, TD, TH, THead, TR } from '../components/ui';

const GATES_KEY = '/api/v1/admin/quality-gates';

// ---------------------------------------------------------------------------
// Valid metric + op enumerations
// ---------------------------------------------------------------------------

const METRICS = [
  'bugs',
  'vulnerabilities',
  'code_smells',
  'security_hotspots',
  'secrets',
  'issues_total',
  'new_bugs',
  'new_vulnerabilities',
  'new_code_smells',
  'new_security_hotspots',
  'new_secrets',
  'new_issues_total',
  'coverage',
  'coverage_on_new_code',
  'loc',
  'files_analyzed',
] as const;

const OPS = [
  { value: 'EQ',  label: '=' },
  { value: 'GT',  label: '>' },
  { value: 'GTE', label: '≥' },
  { value: 'LT',  label: '<' },
  { value: 'LTE', label: '≤' },
] as const;

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function AdminQualityGates() {
  const { data: gates, error, isLoading, mutate } =
    useSWR<QualityGate[]>(GATES_KEY, fetcher, { revalidateOnFocus: false });

  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="max-w-4xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Shield />}
        title="Quality Gates"
        description={
          <>
            Define pass/fail thresholds applied after each scan. Conditions can
            target any metric and optionally scope to new code only.
          </>
        }
        actions={
          <>
            <Link
              to="/admin/settings"
              className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
            >
              <Settings className="h-3.5 w-3.5" aria-hidden="true" />
              Settings
            </Link>
            <button
              onClick={() => setShowCreate(true)}
              className={primaryBtnCls}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              New gate
            </button>
          </>
        }
      />

      {(() => {
        if (isLoading) {
          return <LoadingCard />;
        } else if (error) {
          return <ErrorBanner error={error} />;
        } else if ((gates ?? []).length === 0) {
          return <EmptyState onCreate={() => setShowCreate(true)} />;
        } else {
          return (
            <div className="space-y-4">
              {(gates ?? []).map((g) => (
                <GateCard key={g.id} gate={g} onListMutate={mutate} />
              ))}
            </div>
          );
        }
      })()}

      {showCreate ? (
        <CreateGateDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); mutate(); }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Condition panel — shown inside the expanded gate card
// ---------------------------------------------------------------------------

interface ConditionPanelProps {
  readonly isLoading: boolean;
  readonly detail: QualityGateDetail | undefined;
  readonly gateID: string;
  readonly onMutate: () => void;
}

function ConditionPanel({ isLoading, detail, gateID, onMutate }: ConditionPanelProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-5 py-4 text-fg-muted text-sm">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading conditions…
      </div>
    );
  }
  if (!detail) return null;
  return (
    <ConditionEditor
      gateID={gateID}
      conditions={detail.conditions}
      onMutate={onMutate}
    />
  );
}

// ---------------------------------------------------------------------------
// Gate card — header + expandable condition editor
// ---------------------------------------------------------------------------

interface GateCardProps {
  readonly gate: QualityGate;
  readonly onListMutate: () => void;
}

function GateCard({ gate, onListMutate }: GateCardProps) {
  const [open, setOpen] = useState(false);
  const detailKey = open ? `/api/v1/admin/quality-gates/${gate.id}` : null;
  const { data: detail, isLoading, mutate } =
    useSWR<QualityGateDetail>(detailKey, fetcher, { revalidateOnFocus: false });

  const [deleting, setDeleting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [renameErr, setRenameErr] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!confirm(`Delete gate "${gate.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await adminQualityGatesApi.delete(gate.id);
      onListMutate();
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const handleRename = async (e: FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) { setRenameErr('Name is required.'); return; }
    setRenameErr(null);
    try {
      await adminQualityGatesApi.update(gate.id, { name: newName.trim() });
      onListMutate();
      setRenaming(false);
    } catch (e) {
      setRenameErr(formatApiError(e));
    }
  };

  return (
    <section className="orb-card overflow-hidden">
      {/* Gate header row */}
      <div className="flex items-center gap-2 px-5 py-3 transition-colors">
        {/* Toggle expand/collapse — native button covers the name area */}
        {renaming ? (
          /* Rename form — shown instead of the toggle button */
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {open
              ? <ChevronDown className="h-4 w-4 text-fg-subtle shrink-0" aria-hidden="true" />
              : <ChevronRight className="h-4 w-4 text-fg-subtle shrink-0" aria-hidden="true" />
            }
            {gate.is_default
              ? <Star className="h-3.5 w-3.5 text-warning shrink-0" aria-label="Default gate" />
              : <div className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            }
            <form
              onSubmit={handleRename}
              className="flex items-center gap-2 flex-1"
            >
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className={`${inputCls} text-sm flex-1 max-w-xs`}
                aria-label="Gate name"
              />
              <button type="submit" className={`${primaryBtnCls} py-1`}>
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                Save
              </button>
              <button
                type="button"
                onClick={() => setRenaming(false)}
                className={`${cancelBtnCls} py-1`}
              >
                Cancel
              </button>
              {renameErr ? <span className="text-xs text-danger">{renameErr}</span> : null}
            </form>
          </div>
        ) : (
          <button
            type="button"
            className="flex items-center gap-3 flex-1 min-w-0 text-left hover:bg-bg-inset/50 rounded transition-colors py-1"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open
              ? <ChevronDown className="h-4 w-4 text-fg-subtle shrink-0" aria-hidden="true" />
              : <ChevronRight className="h-4 w-4 text-fg-subtle shrink-0" aria-hidden="true" />
            }
            {gate.is_default
              ? <Star className="h-3.5 w-3.5 text-warning shrink-0" aria-label="Default gate" />
              : <div className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            }
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-fg">{gate.name}</span>
              {gate.is_default
                ? <span className="ml-2 text-xs text-warning">default</span>
                : null
              }
            </div>
          </button>
        )}

        {/* Action buttons — always outside the toggle button to avoid nested buttons */}
        {renaming ? null : (
          <div className="flex items-center gap-1 shrink-0">
            <ActionButton
              onClick={() => { setNewName(gate.name); setRenaming(true); }}
              title="Rename gate"
              icon={<span className="text-xs font-mono leading-none">Aa</span>}
            />
            <ActionButton
              onClick={() => void handleDelete()}
              title="Delete gate"
              icon={deleting
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Trash2 className="h-3.5 w-3.5 text-danger" />
              }
              disabled={deleting}
            />
          </div>
        )}
      </div>

      {/* Condition panel */}
      {open ? (
        <div className="border-t border-border">
          <ConditionPanel
            isLoading={isLoading}
            detail={detail}
            gateID={gate.id}
            onMutate={mutate}
          />
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Condition editor — table of existing conditions + add row
// ---------------------------------------------------------------------------

interface ConditionEditorProps {
  readonly gateID: string;
  readonly conditions: QualityGateCondition[];
  readonly onMutate: () => void;
}

function ConditionEditor({ gateID, conditions, onMutate }: ConditionEditorProps) {
  return (
    <div className="px-5 py-4 space-y-4">
      {conditions.length === 0 ? (
        <p className="text-xs text-fg-subtle italic">No conditions yet. Add one below.</p>
      ) : (
        <Table bare dense>
          <THead>
            <TR>
              <TH>Metric</TH>
              <TH>Op</TH>
              <TH>Threshold</TH>
              <TH>New code only</TH>
              <TH srOnly>Actions</TH>
            </TR>
          </THead>
          <TBody>
            {conditions.map((c) => (
              <ConditionRow
                key={c.id}
                gateID={gateID}
                condition={c}
                onMutate={onMutate}
              />
            ))}
          </TBody>
        </Table>
      )}

      <AddConditionRow gateID={gateID} onAdded={onMutate} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single condition row — inline editing
// ---------------------------------------------------------------------------

interface ConditionRowProps {
  readonly gateID: string;
  readonly condition: QualityGateCondition;
  readonly onMutate: () => void;
}

function ConditionRow({ gateID, condition, onMutate }: ConditionRowProps) {
  const [editing, setEditing] = useState(false);
  const [metric, setMetric] = useState(condition.metric);
  const [op, setOp] = useState(condition.op);
  const [threshold, setThreshold] = useState(String(condition.threshold));
  const [onNewCode, setOnNewCode] = useState(condition.on_new_code);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSave = async () => {
    const t = Number(threshold);
    if (Number.isNaN(t)) { setErr('Threshold must be a number.'); return; }
    setSaving(true);
    setErr(null);
    const patch: QualityGateConditionUpdate = {};
    if (metric !== condition.metric) patch.metric = metric;
    if (op !== condition.op) patch.op = op;
    if (t !== condition.threshold) patch.threshold = t;
    if (onNewCode !== condition.on_new_code) patch.on_new_code = onNewCode;
    try {
      await adminQualityGatesApi.updateCondition(gateID, condition.id, patch);
      setEditing(false);
      onMutate();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminQualityGatesApi.deleteCondition(gateID, condition.id);
      onMutate();
    } catch (e) {
      alert(formatApiError(e));
      setDeleting(false);
    }
  };

  if (!editing) {
    return (
      <TR>
        <TD mono className="text-accent">{condition.metric}</TD>
        <TD primary>
          {OPS.find((o) => o.value === condition.op)?.label ?? condition.op}
        </TD>
        <TD primary>{condition.threshold}</TD>
        <TD>
          {condition.on_new_code
            ? <span className="text-success">yes</span>
            : <span className="text-fg-subtle">no</span>
          }
        </TD>
        <TD>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <ActionButton
              onClick={() => setEditing(true)}
              title="Edit condition"
              icon={<span className="text-xs font-mono leading-none">✎</span>}
            />
            <ActionButton
              onClick={() => void handleDelete()}
              title="Delete condition"
              disabled={deleting}
              icon={deleting
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Trash2 className="h-3 w-3 text-danger" />
              }
            />
          </div>
        </TD>
      </TR>
    );
  }

  return (
    <TR className="bg-bg-inset/60">
      <TD>
        <select aria-label="Metric" value={metric} onChange={(e) => setMetric(e.target.value)} className={`${inputCls} text-xs py-1`}>
          {METRICS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </TD>
      <TD>
        <select aria-label="Operator" value={op} onChange={(e) => setOp(e.target.value)} className={`${inputCls} text-xs py-1 w-16`}>
          {OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </TD>
      <TD>
        <input
          aria-label="Threshold"
          type="number"
          step="any"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          className={`${inputCls} text-xs py-1 w-20`}
        />
      </TD>
      <TD>
        <input
          aria-label="New code only"
          type="checkbox"
          checked={onNewCode}
          onChange={(e) => setOnNewCode(e.target.checked)}
          className="accent-accent"
        />
      </TD>
      <TD>
        <div className="flex items-center gap-1">
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className={`${primaryBtnCls} py-1 text-[11px]`}
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> : null}
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={() => { setEditing(false); setErr(null); }}
            className={`${cancelBtnCls} py-1 text-[11px]`}
          >
            Cancel
          </button>
        </div>
        {err ? <p className="text-xs text-danger mt-1">{err}</p> : null}
      </TD>
    </TR>
  );
}

// ---------------------------------------------------------------------------
// Add condition form row
// ---------------------------------------------------------------------------

interface AddConditionRowProps {
  readonly gateID: string;
  readonly onAdded: () => void;
}

function AddConditionRow({ gateID, onAdded }: AddConditionRowProps) {
  const id = useId();
  const [metric, setMetric] = useState<string>(METRICS[0]);
  const [op, setOp] = useState('EQ');
  const [threshold, setThreshold] = useState('0');
  const [onNewCode, setOnNewCode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    const t = Number(threshold);
    if (Number.isNaN(t)) { setErr('Threshold must be a number.'); return; }
    setSaving(true);
    setErr(null);
    const body: QualityGateConditionCreate = {
      metric, op, threshold: t, on_new_code: onNewCode,
    };
    try {
      await adminQualityGatesApi.addCondition(gateID, body);
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        setThreshold('0');
        setOnNewCode(false);
        onAdded();
      }, 500);
    } catch (e) {
      setErr(formatApiError(e));
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleAdd} className="space-y-2">
      <p className="orb-section-title text-[11px] text-fg-subtle">Add condition</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-metric`} className="text-[11px] text-fg-subtle">Metric</label>
          <select
            id={`${id}-metric`}
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className={`${inputCls} text-xs py-1`}
          >
            {METRICS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-op`} className="text-[11px] text-fg-subtle">Operator</label>
          <select
            id={`${id}-op`}
            value={op}
            onChange={(e) => setOp(e.target.value)}
            className={`${inputCls} text-xs py-1 w-16`}
          >
            {OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-threshold`} className="text-[11px] text-fg-subtle">Threshold</label>
          <input
            id={`${id}-threshold`}
            type="number"
            step="any"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            className={`${inputCls} text-xs py-1 w-20`}
          />
        </div>
        <label htmlFor={`${id}-new-code`} className="flex items-center gap-1.5 text-xs text-fg cursor-pointer pb-1.5">
          <input
            id={`${id}-new-code`}
            type="checkbox"
            checked={onNewCode}
            onChange={(e) => setOnNewCode(e.target.checked)}
            className="accent-accent"
          />
          {'New code only'}
        </label>
        <button
          type="submit"
          disabled={saving || saved}
          className={`${primaryBtnCls} self-end`}
        >
          {(() => {
            if (saved) {
              return <><Check className="h-3.5 w-3.5" aria-hidden="true" /> Added!</>;
            } else if (saving) {
              return <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Adding…</>;
            } else {
              return <><Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add</>;
            }
          })()}
        </button>
      </div>
      {err ? <p role="alert" className="text-xs text-danger">{err}</p> : null}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Create gate dialog
// ---------------------------------------------------------------------------

interface CreateGateDialogProps {
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

function CreateGateDialog({ onClose, onCreated }: CreateGateDialogProps) {
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name is required.'); return; }
    setSaving(true);
    setErr(null);
    try {
      await adminQualityGatesApi.create({ name: name.trim(), is_default: isDefault });
      onCreated();
    } catch (e) {
      setErr(formatApiError(e));
      setSaving(false);
    }
  };

  return (
    <Backdrop onClose={onClose}>
      <Dialog title="New quality gate" onClose={onClose}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogField label="Gate name" htmlFor="qg-name">
            <input
              id="qg-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
              placeholder="Sonar way"
              autoFocus
              required
            />
          </DialogField>

          <label className="flex items-center gap-2 text-xs text-fg cursor-pointer">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="accent-accent"
            />
            {'Set as default gate for this org'}
          </label>

          {err ? <p role="alert" className="text-xs text-danger">{err}</p> : null}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={cancelBtnCls}>Cancel</button>
            <button type="submit" disabled={saving} className={primaryBtnCls}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
              {saving ? 'Creating…' : 'Create gate'}
            </button>
          </div>
        </form>
      </Dialog>
    </Backdrop>
  );
}

// ---------------------------------------------------------------------------
// Shared dialog scaffolding
// ---------------------------------------------------------------------------

function Backdrop({ children, onClose }: { readonly children: React.ReactNode; readonly onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { ref.current?.showModal(); }, []);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop:bg-black/60 p-0 m-0 w-screen h-screen max-w-none max-h-none border-0"
    >
      {children}
    </dialog>
  );
}

function Dialog({
  title,
  onClose,
  children,
}: {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="orb-card w-full max-w-md mx-4 p-6 shadow-2xl">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-lg font-semibold text-fg">{title}</h2>
        <button
          onClick={onClose}
          className="text-fg-muted hover:text-fg transition-colors text-xl leading-none"
          aria-label="Close"
        >
          ×
        </button>
      </div>
      {children}
    </div>
  );
}

function DialogField({
  label,
  htmlFor,
  children,
}: {
  readonly label: string;
  readonly htmlFor: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="orb-section-title text-[11px]">{label}</label>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

function LoadingCard() {
  return (
    <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">Loading gates…</span>
    </div>
  );
}

function EmptyState({ onCreate }: { readonly onCreate: () => void }) {
  return (
    <div className="orb-card p-12 flex flex-col items-center justify-center gap-4 text-center">
      <Shield className="h-10 w-10 text-fg-subtle" aria-hidden="true" />
      <div>
        <p className="text-sm font-medium text-fg">No quality gates yet</p>
        <p className="text-xs text-fg-muted mt-1">
          Create a gate to define pass/fail thresholds applied after each scan.
        </p>
      </div>
      <button onClick={onCreate} className={primaryBtnCls}>
        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        New gate
      </button>
    </div>
  );
}

function ErrorBanner({ error }: { readonly error: unknown }) {
  const msg = formatApiError(error);
  const hint =
    error instanceof ApiError && error.code === 'insufficient_role'
      ? 'Only org admins can manage quality gates.'
      : null;
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared helper
// ---------------------------------------------------------------------------

function ActionButton({
  onClick,
  title,
  icon,
  disabled,
}: {
  readonly onClick: () => void;
  readonly title: string;
  readonly icon: React.ReactNode;
  readonly disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      className="
        p-1.5 rounded hover:bg-bg-subtle transition-colors text-fg-muted
        hover:text-fg disabled:opacity-40 disabled:cursor-not-allowed
      "
    >
      {icon}
    </button>
  );
}

function formatApiError(e: unknown): string {
  if (e instanceof ApiError) {
    return `${e.code}: ${e.message}`;
  } else if (e instanceof Error) {
    return e.message;
  } else {
    return 'request failed';
  }
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const inputCls = `
  w-full px-3 py-2 rounded-md border border-border
  bg-bg-inset text-fg text-sm placeholder-fg-subtle
  focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
  transition-colors
`.trim();

const primaryBtnCls = `
  inline-flex items-center gap-1.5 px-4 py-2 rounded-md
  text-xs font-semibold uppercase tracking-[0.08em]
  bg-accent text-accent-fg hover:opacity-90
  border border-accent/40 transition-opacity
  disabled:opacity-30 disabled:cursor-not-allowed
`.trim();

const cancelBtnCls = `
  inline-flex items-center gap-1.5 px-4 py-2 rounded-md
  text-xs font-semibold uppercase tracking-[0.08em]
  border border-border text-fg-muted hover:text-fg hover:border-fg-muted
  transition-colors
`.trim();
